// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { parseBoolean } from "@tps/XDL/Utilities";
import { Draft } from "immer";

export interface ShipmentPackageApiResultModel {
    shipmentNumber: string;
    orderNumber: string;
    isMtrExist: string;
    isPackingListExist: string;
}

export interface PkgInfo {
    isMtrExist: boolean;
    isPackingListExist: boolean;
}

export interface xOrderDetailsShipmentPkgStateType {
    [key: string]: {
        shipmentPackages: { [shipmentPkgNum: string]: PkgInfo };
        isLoading: boolean;
        failedMessage: string;
    };
}

export const initialOrderDetailsShipmentPkgState: xOrderDetailsShipmentPkgStateType = {};

const reducer = {
    "xOrderDetails/BeginLoadShipmentPackageInfo": (
        draft: Draft<xOrderDetailsShipmentPkgStateType>,
        action: { key: string },
    ) => {
        draft[action.key] = {
            shipmentPackages: {},
            isLoading: true,
            failedMessage: "",
        };
    },
    "xOrderDetails/FailedToLoadShipmentPackageInfo": (
        draft: Draft<xOrderDetailsShipmentPkgStateType>,
        action: { failedMessage: string; key: string },
    ) => {
        draft[action.key] = {
            isLoading: false,
            shipmentPackages: {},
            failedMessage: action.failedMessage,
        };
    },
    "xOrderDetails/CompleteLoadShipmentPackageInfo": (
        draft: Draft<xOrderDetailsShipmentPkgStateType>,
        action: { data: ShipmentPackageApiResultModel[]; key: string },
    ) => {
        if (!draft[action.key]) {
            draft[action.key] = {
                isLoading: false,
                shipmentPackages: {},
                failedMessage: "",
            };
        }

        action.data?.forEach(shipmentPkgLine => {
            if (!draft[action.key].shipmentPackages) {
                draft[action.key].shipmentPackages = {};
            }
            draft[action.key].shipmentPackages[shipmentPkgLine.shipmentNumber] = {
                isMtrExist: parseBoolean(shipmentPkgLine.isMtrExist),
                isPackingListExist: parseBoolean(shipmentPkgLine.isPackingListExist),
            };
        });
        draft[action.key].isLoading = false;
    },
};

const xOrderDetails = createTypedReducerWithImmer(initialOrderDetailsShipmentPkgState, reducer);
(reducers as any).xOrderDetails = xOrderDetails;

export type xOrderDetailsShipmentPkgState = {
    xOrderDetails: xOrderDetailsShipmentPkgStateType;
} & ApplicationState;

type xOrderDetailsShipmentPkgReducers = {
    [key: string]: typeof xOrderDetails;
};

export type xOrderDetailsShipmentPkgActions =
    | Parameters<xOrderDetailsShipmentPkgReducers[keyof xOrderDetailsShipmentPkgReducers]>[1]
    | AnyAction;
